import { useAuthStore } from '@/store/auth.store'

/**
 * Role-based access control (RBAC) configuration and helpers
 * Updated to work with the new auth store structure using branchUserRoleId
 */

// Application roles mapped from branchUserRoleId
export type UserRole = 'Admin' | 'Finance' | 'HR' | 'Employee' | 'Customer'

// Map branchUserRoleId to roles (customize based on your system)
export const ROLE_MAP: Record<number, UserRole[]> = {
  1: ['Admin'], // Admin role
  2: ['Finance'], // Finance role  
  3: ['HR'], // HR role
  4: ['Employee'], // Employee role
  5: ['Customer'], // Customer role
  // Add more mappings as needed
}

// Route access configuration
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/finance': ['finance:read', 'Admin', 'Finance'],
  '/finance/*': ['finance:read', 'Admin', 'Finance'],
  // Finance included: branchUserRoleId 2 maps to Finance; those users must reach HR master data too
  '/hr': ['hr:read', 'Admin', 'HR', 'Finance'],
  '/hr/*': ['hr:read', 'Admin', 'HR', 'Finance'],
  '/employees': ['employees:read', 'Admin', 'HR', 'Finance'],
  '/employees/*': ['employees:read', 'Admin', 'HR', 'Finance'],
  '/emp-documents': ['employees:read', 'Admin', 'HR', 'Finance'],
  '/emp-documents/*': ['employees:read', 'Admin', 'HR', 'Finance'],
  '/customers': ['customers:read', 'finance:read', 'Admin', 'Finance'],
  '/customers/*': ['customers:read', 'finance:read', 'Admin', 'Finance'],
  '/quotations': ['quotations:read', 'finance:read', 'Admin', 'Finance'],
  '/quotations/*': ['quotations:read', 'finance:read', 'Admin', 'Finance'],
  '/invoices': ['invoices:read', 'finance:read', 'Admin', 'Finance'],
  '/invoices/*': ['invoices:read', 'finance:read', 'Admin', 'Finance'],
  '/charts-of-accounts': ['finance:read', 'Admin', 'Finance'],
  '/charts-of-accounts/*': ['finance:read', 'Admin', 'Finance'],
  '/projects': ['finance:read', 'Admin', 'Finance'],
  '/projects/*': ['finance:read', 'Admin', 'Finance'],
}

// Role permissions mapping
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  Admin: ['*'], // Admin has all permissions
  Finance: [
    'finance:read',
    'finance:write',
    'invoices:read',
    'invoices:write',
    'quotations:read',
    'quotations:write',
    'customers:read',
    'customers:write',
    'dashboard:finance',
  ],
  HR: [
    'hr:read',
    'hr:write',
    'employees:read',
    'employees:write',
    'dashboard:hr',
  ],
  Employee: [
    'profile:read',
    'profile:write',
    'dashboard:basic',
  ],
  Customer: [
    'quotations:read',
    'invoices:read',
    'profile:read',
    'profile:write',
  ],
}

/**
 * Get user roles from current auth state
 */
export const getCurrentUserRoles = (): UserRole[] => {
  const { user } = useAuthStore.getState()

  if (!user?.selectedBranch) {
    return []
  }

  const rawId = user.selectedBranch.branchUserRoleId
  const id = Number(rawId)
  const mapped = Number.isFinite(id) ? ROLE_MAP[id] : undefined
  // Unmapped backend role IDs (e.g. 6, 7, …): allow app navigation; APIs still enforce authorization.
  return mapped?.length ? mapped : ['Admin']
}

/**
 * Check if user has specific permission
 */
export const hasPermission = (userRoles: UserRole[], permission: string): boolean => {
  // Handle empty or undefined userRoles
  if (!userRoles || userRoles.length === 0) {
    return false
  }

  // Admin role has all permissions
  if (userRoles.includes('Admin')) {
    return true
  }

  // Check if any user role has the required permission
  return userRoles.some(role => {
    const rolePermissions = ROLE_PERMISSIONS[role] || []
    return rolePermissions.includes(permission) || rolePermissions.includes('*')
  })
}

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (userRoles: UserRole[], requiredRoles: UserRole[]): boolean => {
  // Handle empty or undefined arrays
  if (!userRoles || !requiredRoles || userRoles.length === 0 || requiredRoles.length === 0) {
    return false
  }

  return requiredRoles.some(role => userRoles.includes(role))
}

/**
 * Check if user has sufficient role level
 */
export const hasRoleLevel = (userRoles: UserRole[], minimumLevel: number): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false
  }

  const userLevel = Math.max(...userRoles.map(role => {
    switch (role) {
      case 'Admin': return 100
      case 'Finance': return 80
      case 'HR': return 70
      case 'Employee': return 50
      case 'Customer': return 30
      default: return 0
    }
  }))
  
  return userLevel >= minimumLevel
}

/**
 * Check if user can access specific route
 */
export const canAccessRoute = (userRoles: UserRole[], route: string): boolean => {
  const exactMatch = ROUTE_PERMISSIONS[route]
  const wildcardMatch = ROUTE_PERMISSIONS[`${route.split('/').slice(0, 2).join('/')}/*`]
  const permissions = exactMatch || wildcardMatch

  // Routes not listed here are not gated by this map — allow navigation (RequireAuth still applies).
  if (!permissions) {
    return true
  }

  if (!userRoles || userRoles.length === 0) {
    return false
  }

  return permissions.some(permission => {
    // If permission is a role name, check role directly
    if (Object.values(ROLE_PERMISSIONS).flat().includes(permission as any) === false && 
        ['Admin', 'Finance', 'HR', 'Employee', 'Customer'].includes(permission)) {
      return userRoles.includes(permission as UserRole)
    }
    // Otherwise check permission
    return hasPermission(userRoles, permission)
  })
}

/**
 * Get user's highest role level
 */
export const getUserLevel = (userRoles: UserRole[]): number => {
  if (!userRoles || userRoles.length === 0) {
    return 0
  }

  return Math.max(...userRoles.map(role => {
    switch (role) {
      case 'Admin': return 100
      case 'Finance': return 80
      case 'HR': return 70
      case 'Employee': return 50
      case 'Customer': return 30
      default: return 0
    }
  }))
}

/**
 * Get all permissions for user roles
 */
export const getUserPermissions = (userRoles: UserRole[]): string[] => {
  if (!userRoles || userRoles.length === 0) {
    return []
  }

  const permissions = new Set<string>()
  
  userRoles.forEach(role => {
    const rolePermissions = ROLE_PERMISSIONS[role] || []
    rolePermissions.forEach(permission => permissions.add(permission))
  })
  
  return Array.from(permissions)
}

/**
 * Default redirect routes based on user roles
 */
export const getDefaultRoute = (userRoles: UserRole[]): string => {
  if (!userRoles || userRoles.length === 0) {
    return '/finance'
  }

  if (hasAnyRole(userRoles, ['Admin', 'Finance'])) {
    return '/finance'
  }
  if (hasAnyRole(userRoles, ['HR'])) {
    return '/hr'
  }
  if (hasAnyRole(userRoles, ['Employee'])) {
    return '/finance' // Default dashboard for employees
  }
  return '/finance' // Fallback
}

/**
 * Helper to get current user roles (for use in components)
 */
export const useCurrentUserRoles = (): UserRole[] => {
  return getCurrentUserRoles()
}
